import type { BaseInquiryDetail } from '@models/BookingInquiry';
import type {
  BookingInquiryRoutePlan,
  BookingInquiryCargoLoose,
} from '@models/next/BookingInquiry';
import { BookingInquiryCargoTag } from '@models/next/BookingInquiryEnums';
import {
  BaseBookingInquiryCargoForm,
  BookingInquiryCargoForm,
} from '@models/next/BookingInquiryForms';
import type { BaseRoutePlanSimulator } from '@models/RoutePlan';
import { empty } from '@monoid-dev/mobx-zod-form';
import { formatDate } from '@utils/types';
import dayjs from 'dayjs';

export interface CargoSummary {
  unit: number;
  cbm: number;
  kg: number;
}

export class BookingInquiryHelper {
  static mapToRoutePlan(
    routePlanSimulator: BaseRoutePlanSimulator,
    bookingInquiry: BaseInquiryDetail,
  ): BookingInquiryRoutePlan {
    return {
      pod: routePlanSimulator.route_plan.pod,
      pol: routePlanSimulator.route_plan.pol,
      has_pickups: bookingInquiry.has_pickups,
      pickup_date: bookingInquiry.pickup_date,
      move_in_date: bookingInquiry.move_in_date,
      warehouse_date: routePlanSimulator.simulator.warehouse_date,
      pol_date: routePlanSimulator.simulator.pol_date,
      pod_date: routePlanSimulator.simulator.pod_date,
      delivery_date: formatDate(
        dayjs(
          bookingInquiry.has_pickups ? bookingInquiry.pickup_date : bookingInquiry.move_in_date,
        ).add(routePlanSimulator.simulator.delivery_days, 'day'),
      )!,
      receiver_city: routePlanSimulator.simulator.receiver_city,
      receiving_city: routePlanSimulator.route_plan.receiving_city,
    };
  }

  static getCargoFormDefaultValue(tag: BookingInquiryCargoTag): BookingInquiryCargoForm {
    if (tag === 'LWH') {
      return {
        name: '',
        num: empty,
        pickup_address_index: 0,
        tag,
        length: empty,
        width: empty,
        height: empty,
        weight: empty,
      };
    } else {
      return {
        name: '',
        num: empty,
        pickup_address_index: 0,
        tag,
        total_volume: empty,
        total_weight: empty,
      };
    }
  }

  static mapCargoFormToTag(
    tag: BookingInquiryCargoTag,
    v: BaseBookingInquiryCargoForm,
  ): BookingInquiryCargoForm {
    return {
      ...this.getCargoFormDefaultValue(tag),
      name: v.name,
      num: v.num,
      pickup_address_index: v.pickup_address_index,
    };
  }

  static getCargoSummary(v: BookingInquiryCargoLoose): CargoSummary {
    if (v.tag === 'LWH') {
      const cbm = ((v.num ?? 0) * (v.length ?? 0) * (v.width ?? 0) * (v.height ?? 0)) / 1000_000;

      return {
        unit: v.num ?? 0,
        cbm,
        kg: (v.num ?? 0) * (v.weight ?? 0),
      };
    } else {
      return {
        unit: v.num ?? 0,
        cbm: v.total_volume ?? 0,
        kg: v.total_weight ?? 0,
      };
    }
  }
}
